import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { 
  Plus, Trash2, Edit, Users, Upload, Loader2, Star, Zap,
  Trophy, Shield, Calendar, Flame, Save, X, Crown
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getImageUrl } from "@/lib/imageUtils";

const SpecialGuestsManager = () => {
  const { authAxios } = useAuth();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGuest, setEditingGuest] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    team: "",
    position: "",
    image_url: "",
    bio: "",
    achievements: [],
    visit_date: "",
    special_ability: "",
    power_rating: 85,
    is_featured: false,
    status: "upcoming"
  });
  
  const [achievementInput, setAchievementInput] = useState("");

  useEffect(() => {
    fetchGuests();
  }, []);

  const fetchGuests = async () => {
    try {
      const response = await authAxios.get('/special-guests');
      setGuests(response.data);
    } catch (e) {
      console.error("Failed to fetch guests:", e);
      toast.error("Failed to load special guests");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      title: "",
      team: "",
      position: "",
      image_url: "",
      bio: "",
      achievements: [],
      visit_date: "",
      special_ability: "",
      power_rating: 85,
      is_featured: false,
      status: "upcoming"
    });
    setAchievementInput("");
    setEditingGuest(null);
  };

  const handleEdit = (guest) => {
    setEditingGuest(guest);
    setFormData({
      name: guest.name,
      title: guest.title,
      team: guest.team || "",
      position: guest.position || "",
      image_url: guest.image_url || "",
      bio: guest.bio || "",
      achievements: guest.achievements || [],
      visit_date: guest.visit_date || "",
      special_ability: guest.special_ability || "",
      power_rating: guest.power_rating || 85,
      is_featured: guest.is_featured || false,
      status: guest.status || "upcoming"
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('category', 'guests');

      const response = await authAxios.post('/upload', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData({ ...formData, image_url: response.data.url });
      toast.success("Image uploaded!");
    } catch (e) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const addAchievement = () => {
    if (!achievementInput.trim()) return;
    setFormData({
      ...formData,
      achievements: [...formData.achievements, achievementInput.trim()]
    });
    setAchievementInput("");
  };

  const removeAchievement = (index) => {
    setFormData({
      ...formData,
      achievements: formData.achievements.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.title) {
      toast.error("Name and title are required");
      return;
    }

    try {
      if (editingGuest) {
        await authAxios.put(`/special-guests/${editingGuest.id}`, formData);
        toast.success("Guest updated!");
      } else {
        await authAxios.post('/special-guests', formData);
        toast.success("Guest added!");
      }
      resetForm();
      fetchGuests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save guest");
    }
  };

  const handleDelete = async (guestId) => {
    if (!window.confirm("Delete this special guest?")) return;
    try {
      await authAxios.delete(`/special-guests/${guestId}`);
      toast.success("Guest removed");
      fetchGuests();
    } catch (e) {
      toast.error("Failed to delete guest");
    }
  };

  const getPowerColor = (rating) => {
    if (rating >= 90) return "text-yellow-400";
    if (rating >= 80) return "text-neon-green";
    if (rating >= 70) return "text-neon-blue";
    return "text-gray-400";
  };

  return (
    <div className="space-y-6">
      {/* Add/Edit Form */}
      <div className="cyber-card p-6">
        <h3 className="font-orbitron text-lg text-neon-pink mb-6 flex items-center gap-2">
          {editingGuest ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          {editingGuest ? "EDIT SPECIAL GUEST" : "ADD SPECIAL GUEST"}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Basic Info */}
            <div>
              <label className="font-orbitron text-xs text-gray-400 mb-2 block">NAME *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Marcus Williams"
                className="bg-void-black border-white/10"
                data-testid="guest-name-input"
              />
            </div>
            
            <div>
              <label className="font-orbitron text-xs text-gray-400 mb-2 block">TITLE *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., NFL Pro, College Star"
                className="bg-void-black border-white/10"
                data-testid="guest-title-input"
              />
            </div>
            
            <div>
              <label className="font-orbitron text-xs text-gray-400 mb-2 block">TEAM</label>
              <Input
                value={formData.team}
                onChange={(e) => setFormData({ ...formData, team: e.target.value })}
                placeholder="e.g., Dallas Cowboys"
                className="bg-void-black border-white/10"
              />
            </div>
            
            <div>
              <label className="font-orbitron text-xs text-gray-400 mb-2 block">POSITION</label>
              <Input
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                placeholder="e.g., Wide Receiver"
                className="bg-void-black border-white/10"
              />
            </div>
            
            <div>
              <label className="font-orbitron text-xs text-gray-400 mb-2 block">SPECIAL ABILITY</label>
              <Input
                value={formData.special_ability}
                onChange={(e) => setFormData({ ...formData, special_ability: e.target.value })}
                placeholder="e.g., SPEED DEMON, ARM CANNON"
                className="bg-void-black border-white/10"
              />
            </div>
            
            <div>
              <label className="font-orbitron text-xs text-gray-400 mb-2 block">VISIT DATE</label>
              <Input
                value={formData.visit_date}
                onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                placeholder="e.g., January 25, 2025"
                className="bg-void-black border-white/10"
              />
            </div>
            
            <div>
              <label className="font-orbitron text-xs text-gray-400 mb-2 block">
                POWER RATING: <span className={getPowerColor(formData.power_rating)}>{formData.power_rating}</span>
              </label>
              <input
                type="range"
                min="1"
                max="99"
                value={formData.power_rating}
                onChange={(e) => setFormData({ ...formData, power_rating: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-neon-pink"
              />
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>1</span>
                <span>50</span>
                <span>99</span>
              </div>
            </div>
            
            <div>
              <label className="font-orbitron text-xs text-gray-400 mb-2 block">STATUS</label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger className="bg-void-black border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-void-black border-white/10">
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Bio */}
          <div>
            <label className="font-orbitron text-xs text-gray-400 mb-2 block">BIO</label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Brief bio about the guest..."
              rows={3}
              className="w-full bg-void-black border border-white/10 rounded px-3 py-2 text-white font-rajdhani resize-none focus:border-neon-pink/50 focus:outline-none"
            />
          </div>
          
          {/* Achievements */}
          <div>
            <label className="font-orbitron text-xs text-gray-400 mb-2 block">ACHIEVEMENTS</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={achievementInput}
                onChange={(e) => setAchievementInput(e.target.value)}
                placeholder="e.g., Super Bowl Champion"
                className="bg-void-black border-white/10 flex-1"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAchievement())}
              />
              <button
                type="button"
                onClick={addAchievement}
                className="px-4 py-2 bg-neon-pink/20 border border-neon-pink/50 text-neon-pink font-orbitron text-xs hover:bg-neon-pink/30 transition-colors"
              >
                ADD
              </button>
            </div>
            {formData.achievements.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.achievements.map((ach, i) => (
                  <div key={i} className="flex items-center gap-1 bg-neon-pink/10 border border-neon-pink/30 px-2 py-1 rounded">
                    <Trophy className="w-3 h-3 text-neon-pink" />
                    <span className="font-rajdhani text-xs text-neon-pink">{ach}</span>
                    <button
                      type="button"
                      onClick={() => removeAchievement(i)}
                      className="ml-1 text-gray-500 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Image Upload */}
          <div>
            <label className="font-orbitron text-xs text-gray-400 mb-2 block">PHOTO</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="guest-image-upload"
            />
            
            <div className="flex gap-4 items-start">
              {formData.image_url && (
                <div className="relative w-24 h-32 rounded overflow-hidden border-2 border-neon-blue/50">
                  <img src={getImageUrl(formData.image_url)} alt="Preview" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 border-2 border-neon-pink/50" />
                </div>
              )}
              
              <div className="flex-1 space-y-2">
                <label
                  htmlFor="guest-image-upload"
                  className={`block border-2 border-dashed border-white/20 hover:border-neon-blue/50 p-4 rounded text-center cursor-pointer transition-colors ${uploading ? 'opacity-50' : ''}`}
                >
                  {uploading ? (
                    <Loader2 className="w-6 h-6 text-neon-blue mx-auto animate-spin" />
                  ) : (
                    <Upload className="w-6 h-6 text-gray-500 mx-auto" />
                  )}
                  <p className="font-rajdhani text-xs text-gray-500 mt-1">
                    {uploading ? "Uploading..." : "Upload photo"}
                  </p>
                </label>
                
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="Or paste image URL"
                  className="bg-void-black border-white/10 text-xs"
                />
              </div>
            </div>
          </div>
          
          {/* Featured toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, is_featured: !formData.is_featured })}
              className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${
                formData.is_featured 
                  ? 'bg-yellow-500/20 border border-yellow-500/50 text-yellow-400'
                  : 'bg-white/5 border border-white/20 text-gray-400'
              }`}
            >
              <Crown className="w-4 h-4" />
              <span className="font-orbitron text-xs">FEATURED GUEST</span>
            </button>
          </div>
          
          {/* Form Actions */}
          <div className="flex gap-3">
            <button
              type="submit"
              className="cyber-btn py-3 px-6 flex-1"
              data-testid="save-guest-btn"
            >
              <span className="flex items-center justify-center gap-2">
                <Save className="w-4 h-4" />
                {editingGuest ? "UPDATE GUEST" : "ADD GUEST"}
              </span>
            </button>
            
            {editingGuest && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 border border-white/20 text-gray-400 font-orbitron text-xs hover:bg-white/5 transition-colors"
              >
                CANCEL
              </button>
            )}
          </div>
        </form>
      </div>
      
      {/* Guests List */}
      <div className="cyber-card p-6">
        <h3 className="font-orbitron text-lg text-neon-blue mb-6 flex items-center gap-2">
          <Users className="w-5 h-5" />
          SPECIAL GUESTS ({guests.length})
        </h3>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-neon-blue animate-spin" />
          </div>
        ) : guests.length === 0 ? (
          <p className="text-gray-500 text-center py-8 font-rajdhani">No special guests added yet</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {guests.map((guest) => (
              <div 
                key={guest.id}
                className="relative bg-gray-900/50 border border-white/10 rounded-lg overflow-hidden hover:border-neon-blue/50 transition-colors group"
              >
                {/* Image */}
                <div className="relative h-32 bg-gray-800">
                  {guest.image_url ? (
                    <img 
                      src={getImageUrl(guest.image_url)} 
                      alt={guest.name} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Users className="w-12 h-12 text-gray-600" />
                    </div>
                  )}
                  
                  {/* Overlay actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleEdit(guest)}
                      className="w-10 h-10 bg-neon-blue/20 border border-neon-blue/50 rounded-full flex items-center justify-center hover:bg-neon-blue/40"
                    >
                      <Edit className="w-4 h-4 text-neon-blue" />
                    </button>
                    <button
                      onClick={() => handleDelete(guest.id)}
                      className="w-10 h-10 bg-red-500/20 border border-red-500/50 rounded-full flex items-center justify-center hover:bg-red-500/40"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                  
                  {/* Power rating badge */}
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/80 px-2 py-1 rounded">
                    <Flame className="w-3 h-3 text-orange-500" />
                    <span className={`font-orbitron text-sm font-bold ${getPowerColor(guest.power_rating)}`}>
                      {guest.power_rating}
                    </span>
                  </div>
                  
                  {/* Featured badge */}
                  {guest.is_featured && (
                    <div className="absolute top-2 right-2">
                      <Crown className="w-5 h-5 text-yellow-400 drop-shadow-lg" />
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <div className="p-3">
                  <h4 className="font-orbitron text-sm text-white truncate">{guest.name}</h4>
                  <p className="font-rajdhani text-xs text-neon-pink truncate">{guest.title}</p>
                  {guest.team && (
                    <p className="font-rajdhani text-xs text-gray-400 truncate">{guest.team}</p>
                  )}
                  
                  {/* Status */}
                  <div className="mt-2">
                    <span className={`text-[10px] font-orbitron px-2 py-0.5 rounded ${
                      guest.status === 'confirmed' ? 'bg-neon-green/20 text-neon-green' :
                      guest.status === 'upcoming' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {guest.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialGuestsManager;
